// Package imports:
import React, { useMemo, useRef, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
// Component imports:
import Alert from '../../../ui-elements/Alert/Alert';
import Button from '../../../ui-elements/Button/Button';
import Input from '../../../ui-elements/Input/Formik/Input';
import Link from '../../../ui-elements/Link/Link';
import Table from '../../../ui-elements/Table/Table';
import DisplayBox from '../../DisplayBox/DisplayBox';
import LockIcon from '../../../ui-elements/LockIcon/LockIcon';
import ExampleReportButton from '../../ExampleReportButton/ExampleReportButton';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { useBuyingProcess } from '../../../services/buyhook';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { IKeldanApiResponse } from '../../../types/KeldanTypes';
import { IPropertySearchResult } from '../../../types/PropertyTypes';
import { IisAuthenticated } from '../../../types/Types';

interface IBuyGangverd {
    whereClick: 'form' | 'list',
    fastanr: string,
    reference?: string
}

const GangverdSearch: React.FC<IisAuthenticated> = ({
    isAuthenticated
}) => {
    const [isSearchFormLoading, setIsSearchFormLoading] = useState(false);
    const [searchButtonLoading, setSearchButtonLoading] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<IPropertySearchResult>();

    const searchResultsRef = useRef<HTMLDivElement | null>(null);

    const defaultSearchTerm = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const fastanr = urlParams.get('fastanr');
        return (fastanr === null || fastanr === '') ? null : fastanr;
    }, [])

    const { tryPurchaseItem, tryToBuyItem, resetState, setToastError, ModalsAndToasts } = useBuyingProcess(async (values: IBuyGangverd, modalReference: string) => {
        try {
            const { fastanr, reference } = values;
            const url = `${GET_KELDAN_API_URL()}/Fasteignir/Gangverd-Kaupa`;
            const query = new URLSearchParams({
                fastanr: fastanr,
                reference: reference ?? modalReference
            });
            const response = await fetch(`${url}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: query
            });
    
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse<IPropertySearchResult> = await response.json();
                const { message } = responseBody;
                if (message === ErrorMessages.NoCard) {
                    resetState('no card');
                } else if (message) resetState({
                    type: 'alert',
                    headText: message
                });
                else resetState('bad request');
            } else {
                resetState('response not ok')
            }
        } catch (e) {
            resetState('network error');
        }
    }, 'Verðvísi Gangverðs');

    const handleSearch = async (searchTerm: string, whereClick: 'list' | 'form') => {
        try {
            const requestQuery = new URLSearchParams({
                term: searchTerm
            });
            const url = (whereClick === 'form')
                ? `${GET_KELDAN_API_URL()}/Property/PropertySearchAll`
                : `${GET_KELDAN_API_URL()}/Property/GangverdSearch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            // Done after the request for better user experience.
            setSearchTerm(searchTerm);
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const responseBody: IKeldanApiResponse<IPropertySearchResult> = await response.json();
                    const { message, result } = responseBody;
                    if (result) {
                        setSearchResults(responseBody.result);
                        searchResultsRef.current?.scrollIntoView();
                    }
                    else if (message === ErrorMessages.NoCard) {
                        setToastError('no card');
                    } else if (message) {
                        setToastError({
                            type: 'alert',
                            headText: message
                        })
                    } else {
                        setToastError('bad request');
                    }
                }
            } else {
                setToastError('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            setToastError('network error');
        }
        // Reset all loading states.
        setSearchButtonLoading(null)
        setIsSearchFormLoading(false)
    }

    return (
        <div className='KCL_property-search-form'>
            <div className="shell">
                <h3>
                    {!isAuthenticated && <LockIcon size='lg' />}
                    Verðvísir Gangverðs
                </h3>
                <div className='property-form-description'>
                    <p>Gangverð hefur þróað vöruna Verðvísi fyrir aðila í eignaviðskiptum og fjármögnun sem gefur betri upplýsingar en áður hafa verið aðgengilegar um verð og verðþróun fasteigna. Verðvísirinn er uppfærðu mánaðarlega til samræmis við þróun eignamarkaða þann mánuðinn.</p>
                    <p className='bold-section'> Hér er hægt að kaupa upplýsingar úr Verðvísinum fyrir eina ákveðna fasteign. <Link linkSize="18" href="/Verdskra">Sjá verðskrá.</Link></p>
                </div>
                <Formik
                    initialValues={{
                        term: defaultSearchTerm ?? '',
                        tilvisun: ''
                    }}
                    validationSchema={Yup.object({
                        term: Yup.string().required('Fastanúmer má ekki vera tómt'),
                        tilvisun: Yup.string()
                    })}
                    onSubmit={({ term, tilvisun }) => tryPurchaseItem({
                        whereClick: 'form',
                        fastanr: term,
                        reference: tilvisun
                    })}
                    component={(props: { values: IPurchasingFormValues }) => (
                        <PurchasingSubFormComponent
                            isAuthenticated={isAuthenticated}
                            values={props.values}
                            loading={(
                                tryToBuyItem !== null
                                && tryToBuyItem.whereClick === 'form'
                            )}
                        />
                    )}
                />
                <p>Einnig er hægt að leita eftir fasteign</p>
                <Formik
                    initialValues={{
                        searchterm: ''
                    }}
                    validationSchema={Yup.object({
                        searchterm: Yup.string().required('Leit má ekki vera tóm')
                    })}
                    onSubmit={(values) => {
                        setIsSearchFormLoading(true);
                        handleSearch(values.searchterm, 'form');
                    }}
                    component={(props: { values: ISearchFormValues }) => (
                        <SearchSubFormComponent
                            isAuthenticated={isAuthenticated}
                            loading={isSearchFormLoading}
                            values={props.values}
                        />
                    )}
                />
                <ExampleReportButton size='lg' reportType='gangverd' />
            </div>

            {(searchResults !== undefined) && 
                <div className="search-results section--gray" ref={searchResultsRef}>
                    <div className="shell">
                    <h2>Leitarniðurstöður fyrir {searchTerm}</h2>
                    {searchResults?.response?.properties?.properties
                        ? (
                            <DisplayBox>
                                <Table
                                    tableSize="lg"
                                    data={searchResults.response.properties.properties}
                                    columns={[{
                                        title: 'Fastanúmer',
                                        dataAccessor: ({property_number}) => property_number,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Heiti',
                                        dataAccessor: () => searchResults.response?.properties?.address?.street,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Húsnúmer',
                                        dataAccessor: () => searchResults.response?.properties?.address?.house_number,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Merking',
                                        dataAccessor: ({units}) => units[0].tag,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Sveitarfélag',
                                        dataAccessor: () => searchResults.response?.properties?.address?.county,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Byggð',
                                        dataAccessor: () => searchResults.response?.properties?.address?.city,
                                        textAlign: 'left'
                                    }, {
                                        title: '',
                                        dataAccessor: ({property_number}) => (
                                            <Button
                                                buttonType="buy"
                                                size='sm'
                                                showLoader={(
                                                    tryToBuyItem !== null
                                                    && tryToBuyItem.whereClick === 'list'
                                                    && tryToBuyItem.fastanr === property_number
                                                )}
                                                onClick={() => {
                                                    if (property_number === undefined) {
                                                        setToastError({
                                                            type: 'alert',
                                                            headText: 'Villa í fastanúmeri'
                                                        })
                                                    } else {
                                                        tryPurchaseItem({
                                                            whereClick: 'list',
                                                            fastanr: property_number
                                                        }, 'confirm-modal-with-reference')
                                                    }
                                                }}
                                            >
                                                Kaupa
                                            </Button>
                                        )
                                    }]}
                                />
                            </DisplayBox>
                        )
                        : (searchResults?.response?.addresses)
                        ? (
                            <DisplayBox>
                                <Table
                                    tableSize="lg"
                                    data={searchResults.response.addresses}
                                    columns={[{
                                        title: 'Heitisnúmer',
                                        dataAccessor: ({address: {name_number}}) => name_number,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Heiti',
                                        dataAccessor: ({address: {street}}) => street,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Húsnúmer',
                                        dataAccessor: ({address: {house_number}}) => house_number,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Sveitarfélag',
                                        dataAccessor: ({address: {county}}) => county,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Byggð',
                                        dataAccessor: ({address: {city}}) => city,
                                        textAlign: 'left'
                                    }, {
                                        title: '',
                                        dataAccessor: ({address: {name_number}}) => (
                                            <Button
                                                buttonType="secondary"
                                                size='sm'
                                                showLoader={(
                                                    searchButtonLoading !== null
                                                    && searchButtonLoading === name_number)}
                                                onClick={() => {
                                                    if (name_number === undefined) {
                                                        setToastError({
                                                            type: 'alert',
                                                            headText: 'Villa í heitisnúmeri'
                                                        })
                                                    } else {
                                                        setSearchButtonLoading(name_number);
                                                        handleSearch(name_number, 'list');
                                                    }
                                                }}
                                            >
                                                Skoða
                                            </Button>
                                        )
                                    }]}
                                />
                            </DisplayBox>
                        )
                        : <Alert type='info' headText="Engar niðurstöður fundust" />
                    }
                    </div>
                </div>
            }
            <ModalsAndToasts />
        </div>
    );
}

export default GangverdSearch;


interface ISearchFormValues {
    searchterm: string
}

interface ISearchSubFormProps {
    isAuthenticated?: boolean,
    values: ISearchFormValues,
    loading: boolean,
}

const SearchSubFormComponent: React.FC<ISearchSubFormProps> = ({
    isAuthenticated,
    values,
    loading
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                className='input-term'
                                placeholder="Leita eftir götuheiti, fastanúmeri eða heitisnúmeri"
                                name="searchterm"
                                id="searchterm"
                                value={values.searchterm}
                                disabled={!isAuthenticated}
                            />
                        </div>
                        <div className="form__col actions">
                            <Button
                                showLoader={loading}
                                size="lg"
                                style={{'margin': 0}}
                                disabled={values.searchterm === ''}
                            >
                                Leita
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    )
}

interface IPurchasingFormValues {
    term: string,
    tilvisun: string
}

interface IPurchasingSubFormProps {
    isAuthenticated?: boolean,
    values: IPurchasingFormValues,
    loading: boolean,
}

const PurchasingSubFormComponent: React.FC<IPurchasingSubFormProps> = ({
    isAuthenticated,
    values,
    loading
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                className='input-term'
                                placeholder='Fastanúmer'
                                name="term"
                                id="term"
                                value={values.term}
                                disabled={!isAuthenticated}
                            />
                        </div>
                        <div className="form__col">
                            <Input
                                className='input-tilvisun'
                                placeholder='Tilvísun á reikning'
                                name="tilvisun"
                                id="tilvisun"
                                value={values.tilvisun}
                                disabled={!isAuthenticated}
                            />
                        </div>
                        <div className="form__col actions">
                            <Button
                                showLoader={loading}
                                buttonType="buy"
                                size="lg"
                                style={{'margin': 0}}
                                disabled={values.term === ''}
                            >
                                Kaupa
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    )
}